// src/context/SectionColorContext.js
import React, { createContext, useState, useContext, useEffect, useCallback, useMemo } from 'react';
import api from '../api/axiosConfig';
import { useAuth } from './AuthContext';
import hexToRgba from '../utils/hexToRgba';
import { ACTION_ICON_KEYS } from '../components/SectionIcon';

// Custom hover-color overrides for navbar items and hub tiles — a single
// global map of { key: '#rrggbb' }, fetched once and shared app-wide.
// Sections with no entry just use the app's default accent hover color.
const SectionColorContext = createContext({ colors: {}, refresh: () => {} });

export const useSectionColors = () => useContext(SectionColorContext);

// A saved color only ever comes from an <input type="color"> (SettingsPage),
// which the browser normalizes to '#rrggbb' — this just guards against
// injecting anything unexpected into the raw CSS text below.
const HEX_COLOR_RE = /^#[0-9a-fA-F]{6}$/;

// Action icons (View/Refresh/Add/Edit/Delete/Descargar/Subir/Limpiar) have
// no tile of their own the way a nav item or hub tile does — their icon is
// just a small glyph sitting inside someone else's button. `:has()` lets a
// single global rule reach from that glyph up to whichever button/link
// contains it and tint the whole thing on hover, without touching any of
// the pages that render the icon.
//
// In a "Filtros y Acciones" panel (.pf-filters) EVERY button gets this same
// highlight, not just the ones holding an action icon: CollapsibleFilters.css
// adds a zero-specificity indigo fallback for the buttons with no color set
// here, so the colors configured in Settings still win for the icon buttons.
const actionIconCss = (colors) =>
  Object.entries(colors)
    .filter(([key, color]) => ACTION_ICON_KEYS.has(key) && HEX_COLOR_RE.test(color))
    .map(([key, color]) => {
      const cls = `.action-icon-${key}`;
      return `
        button:has(${cls}):hover:not(:disabled), a:has(${cls}):hover, [role="button"]:has(${cls}):hover {
          background: ${hexToRgba(color, 0.16)} !important;
          box-shadow: inset 0 0 0 1px ${hexToRgba(color, 0.7)} !important;
          border-radius: 6px !important;
        }`;
    })
    .join('\n');

export const SectionColorProvider = ({ children }) => {
  const { user } = useAuth();
  const [colors, setColors] = useState({});

  const refresh = useCallback(() => {
    api.get('settings/colors/')
      .then(r => setColors(r.data || {}))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (user) refresh();
  }, [user, refresh]);

  const actionIconStyle = useMemo(() => actionIconCss(colors), [colors]);

  return (
    <SectionColorContext.Provider value={{ colors, refresh }}>
      <style>{actionIconStyle}</style>
      {children}
    </SectionColorContext.Provider>
  );
};
